import { createStarQuest, starCharts, type StarQuestState } from "../components/PresentPlayground/stargazing/starQuest";
import { PAINT_SIZE } from "../rendering/present/bloomPainting";
import { bloomInk, type BloomPaletteSelection } from "../rendering/present/bloomPalette";
import type { BloomTurn } from "../rendering/present/bloomTurnState";

export const PROGRESS_KEY = "birthday-experience.draft.v1";
export const chapters = ["arrival", "starlight", "bloom", "wish"] as const;
export type Chapter = typeof chapters[number];
export interface SavedFlower {
  phase: "bud" | "painting" | "open";
  atlas: string;
  palette: BloomPaletteSelection;
  customColor: string;
  turn: BloomTurn;
}
export interface BirthdayProgress {
  version: 1;
  chapter: Chapter;
  stars: StarQuestState;
  flower: SavedFlower | null;
  previousFlower: SavedFlower | null;
}
const atlasBytes = PAINT_SIZE * PAINT_SIZE * 4;
const atlasLength = 4 * Math.ceil(atlasBytes / 3);
const validAtlas = (value: unknown): value is string => typeof value === "string" &&
  (value === "" || (value.length === atlasLength && /^[A-Za-z0-9+/]+==$/.test(value)));

export function encodeAtlas(pixels: Uint8Array) {
  if (!pixels.some((value, index) => index % 4 === 3 && value > 0)) return "";
  let binary = "";
  for (let i = 0; i < pixels.length; i += 8192) binary += String.fromCharCode(...pixels.subarray(i, i + 8192));
  return btoa(binary);
}
export function decodeAtlas(value: string) {
  if (!validAtlas(value)) throw new Error("Invalid flower atlas");
  if (!value) return new Uint8Array(atlasBytes);
  const binary = atob(value);
  return Uint8Array.from(binary, character => character.charCodeAt(0));
}

/** A selected brush or a backed-up flower is not the visitor's current artwork. */
export function hasPaintedFlower(flower: SavedFlower | null | undefined) {
  if (!flower || flower.phase === "bud" || !flower.atlas) return false;
  try {
    return decodeAtlas(flower.atlas).some((value, index) => index % 4 === 3 && value > 0);
  } catch { return false; }
}
const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
const indices = (value: unknown, size: number): value is number[] => Array.isArray(value) &&
  value.length <= size && new Set(value).size === value.length && value.every(id => Number.isInteger(id) && id >= 0 && id < size);
function readStars(value: unknown): StarQuestState | null {
  if (!record(value) || !Number.isInteger(value.round)) return null;
  const round = value.round as number, chart = starCharts[round];
  if (!chart || !indices(value.found, chart.stars.length) || !indices(value.placed, chart.stars.length) ||
    !indices(value.completed, starCharts.length) || !Number.isSafeInteger(value.misses) || (value.misses as number) < 0 ||
    !["search", "observe", "assemble", "complete"].includes(value.phase as string)) return null;
  const { found, placed, completed } = value;
  if (placed.some(id => !found.includes(id)) || (value.phase === "search" && placed.length) ||
    (value.phase !== "search" && found.length !== chart.stars.length) ||
    (value.phase === "complete" && (placed.length !== chart.stars.length || !completed.includes(round))) ||
    (value.phase !== "complete" && placed.length === chart.stars.length) ||
    completed.some(id => id > round) || Array.from({ length: round }, (_, id) => id).some(id => !completed.includes(id))) return null;
  return { round, phase: value.phase as StarQuestState["phase"], found, placed, completed, misses: value.misses as number };
}
function readFlower(value: unknown): SavedFlower | null {
  if (!record(value) || !["bud", "painting", "open"].includes(value.phase as string) || !validAtlas(value.atlas) ||
    !(value.palette === "custom" || (Number.isInteger(value.palette) && !!bloomInk[value.palette as number])) ||
    typeof value.customColor !== "string" || !/^#[0-9a-f]{6}$/i.test(value.customColor) || !record(value.turn)) return null;
  const { x, y, z, w } = value.turn;
  if (![x, y, z, w].every(n => typeof n === "number" && Number.isFinite(n))) return null;
  const turn = { x, y, z, w } as BloomTurn;
  const length = Math.hypot(turn.x, turn.y, turn.z, turn.w);
  if (length < 0.99 || length > 1.01 || (value.phase === "open" && !value.atlas)) return null;
  return { phase: value.phase as SavedFlower["phase"], atlas: value.atlas, palette: value.palette as BloomPaletteSelection,
    customColor: value.customColor, turn };
}
export function parseProgress(raw: string | null): BirthdayProgress | null {
  if (!raw || raw.length > 800000) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (!record(value) || value.version !== 1 || !chapters.includes(value.chapter as Chapter)) return null;
    const stars = readStars(value.stars), flower = readFlower(value.flower), previousFlower = readFlower(value.previousFlower);
    if (!stars || (value.flower !== null && !flower) || (value.previousFlower !== null && !previousFlower)) return null;
    // Only explicit artwork/progress fields survive. Credentials and playback are never retained.
    return { version: 1, chapter: value.chapter as Chapter, stars, flower, previousFlower };
  } catch { return null; }
}
const emptyProgress = (): BirthdayProgress => ({ version: 1, chapter: "arrival", stars: createStarQuest(), flower: null, previousFlower: null });
type DraftStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** Lazy: the entry gate does not read or unlock this draft. A failed write preserves in-page work. */
export function createProgressStore(storage: () => DraftStorage) {
  let loaded = false, draft: BirthdayProgress | null = null, durable = true;
  const listeners = new Set<() => void>();
  const report = (next: boolean) => { if (durable !== next) { durable = next; listeners.forEach(listener => listener()); } return next; };
  const read = () => {
    if (!loaded) {
      loaded = true;
      try { draft = parseProgress(storage().getItem(PROGRESS_KEY)); } catch { report(false); }
    }
    return draft;
  };
  return {
    read,
    patch(changes: Partial<Omit<BirthdayProgress, "version">>) {
      draft = { ...(read() ?? emptyProgress()), ...changes };
      try { storage().setItem(PROGRESS_KEY, JSON.stringify(draft)); return report(true); } catch { return report(false); }
    },
    clear() {
      loaded = true; draft = null;
      try { storage().removeItem(PROGRESS_KEY); return report(true); } catch { return report(false); }
    },
    isDurable: () => durable,
    subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
  };
}
export const birthdayProgress = createProgressStore(() => window.localStorage);
export function hasProgress(draft: BirthdayProgress | null) {
  return !!draft && (draft.chapter !== "arrival" || draft.stars.found.length > 0 || draft.stars.round > 0 || !!draft.flower || !!draft.previousFlower);
}
export function collectedFromQuest(quest: StarQuestState) {
  return quest.round > 0 || quest.completed.length > 0 ? [0, 1, 2, 3, 4] : quest.found.filter(id => id < 5);
}
